import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e8cdf70f-00bc-4528-a3bf-1c9ff1127e96/artifacts/zn03why2_lv_0_20260214074351.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "About", path: "/about" },
  { name: "Gallery", path: "/gallery" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Pricing", path: "/pricing" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-6xl transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 shadow-2xl shadow-emerald-900/10"
          : "bg-white/90"
      } backdrop-blur-xl rounded-full border border-white/20`}
    >
      <div className="flex items-center justify-between px-4 md:px-6 py-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Toronto Premium Cleaning"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              className={`nav-link px-4 py-2 text-sm font-medium transition-colors ${
                isActive(link.path)
                  ? "text-[#064E3B]"
                  : "text-[#78716C] hover:text-[#064E3B]"
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+16478700445"
            className="flex items-center gap-2 text-sm font-medium text-[#064E3B] hover:text-[#065F46] transition-colors"
            data-testid="phone-link"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden xl:inline">(647) 870-0445</span>
          </a>
          <Link to="/booking" data-testid="book-now-btn">
            <Button className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-6 py-2 text-sm font-medium shadow-lg shadow-emerald-900/20 transition-all hover:scale-[1.02]">
              Book Now
            </Button>
          </Link>
        </div>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6 text-[#064E3B]" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] bg-white p-0">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b border-[#E7E5E4]">
                <img
                  src={LOGO_URL}
                  alt="Toronto Premium Cleaning"
                  className="h-10 w-auto object-contain"
                />
              </div>
              <div className="flex flex-col py-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`px-6 py-4 text-base font-medium border-b border-[#E7E5E4]/50 transition-colors ${
                      isActive(link.path)
                        ? "text-[#064E3B] bg-[#F0FDF4]"
                        : "text-[#78716C] hover:text-[#064E3B] hover:bg-[#F0FDF4]/50"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
              <div className="mt-auto p-6 space-y-4 border-t border-[#E7E5E4]">
                <a
                  href="tel:+16478700445"
                  className="flex items-center gap-3 text-[#064E3B] font-medium"
                >
                  <Phone className="w-5 h-5" />
                  (647) 870-0445
                </a>
                <Link to="/booking" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full py-6 text-base font-medium">
                    Book Now
                  </Button>
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
